import React from 'react';
import { Card } from '@heroui/react';
import { Node as SlateNode } from 'slate';
import { JOB_STATUS_LABELS, guardJobStatus } from '../../constants/jobStatus';
import { SalesDashboardJobDetails } from '../../services/api/dashboardController';
import { formatSalaryRange } from '../dashboard/Shared/formatSalaryRange';
import RichTextRenderer from '../../utils/RichTextRenderer';
import { RichTextValue } from '../../utils/richText';

const numberFormatter = new Intl.NumberFormat('en-US');
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatCount = (value: number | null | undefined) =>
  numberFormatter.format(
    typeof value === 'number' && Number.isFinite(value) ? value : 0,
  );

const formatMaybeDate = (value: number | null | undefined) => {
  if (!value || !Number.isFinite(value)) {
    return 'Unknown';
  }

  try {
    return dateFormatter.format(new Date(value));
  } catch (_error) {
    return 'Unknown';
  }
};

const hasRenderableRichText = (value: RichTextValue | undefined) => {
  if (!value?.length) {
    return false;
  }

  return value.some((node) => SlateNode.string(node).trim().length > 0);
};

type SalesJobDetailsPanelProps = {
  jobDetails: SalesDashboardJobDetails;
  requestedDisplayJobId: string;
  headingLabel?: string;
  timestampLabel?: string;
};

const DEFAULT_HEADING_LABEL = 'Matched job';
const DEFAULT_TIMESTAMP_LABEL = 'Query timestamp';

const SalesJobDetailsPanel = ({
  jobDetails,
  requestedDisplayJobId,
  headingLabel = DEFAULT_HEADING_LABEL,
  timestampLabel = DEFAULT_TIMESTAMP_LABEL,
}: SalesJobDetailsPanelProps) => {
  const statusKey = jobDetails.status
    ? guardJobStatus(jobDetails.status)
    : null;
  const compensationLabel =
    formatSalaryRange({
      minPay: jobDetails.minPay,
      maxPay: jobDetails.maxPay,
      currency: jobDetails.currency,
      salaryPeriod: jobDetails.salaryPeriod,
    }) || 'Not provided';

  const jobDescription =
    (jobDetails.jobDescription as RichTextValue | undefined) || undefined;
  const workspaceDescription =
    (jobDetails.workspaceDescription as RichTextValue | undefined) || undefined;
  const hasJobDescription = hasRenderableRichText(jobDescription);
  const hasWorkspaceDescription = hasRenderableRichText(workspaceDescription);

  return (
    <div className="space-y-5">
      <Card className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,#FFFFFF_0%,#F8FBFF_100%)] p-6 shadow-[0_22px_60px_rgba(15,23,42,0.08)] sm:p-7">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[360px] bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.16),transparent_64%)]" />

        <div className="relative flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 xl:max-w-[760px]">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex rounded-full border border-slate-200 bg-white/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                {headingLabel}
              </span>
              {statusKey ? (
                <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">
                  {JOB_STATUS_LABELS[statusKey]}
                </span>
              ) : null}
            </div>

            <h2
              className="mt-4 text-[32px] leading-[1.02] tracking-[-0.04em] text-slate-900 sm:text-[38px]"
              style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
            >
              {jobDetails.jobTitle || 'Untitled job'}
            </h2>

            <div className="mt-4 flex flex-wrap gap-2.5">
              <span className="inline-flex rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-semibold text-slate-800">
                Job ID {jobDetails.displayJobId || requestedDisplayJobId}
              </span>
              {jobDetails.location ? (
                <span className="inline-flex rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm text-slate-700">
                  {jobDetails.location}
                </span>
              ) : null}
              {jobDetails.employmentType ? (
                <span className="inline-flex rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm text-slate-700">
                  {jobDetails.employmentType}
                </span>
              ) : null}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[22px] border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Compensation
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {compensationLabel}
                </div>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Workspace
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.workspaceName || 'Not provided'}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:w-[360px] xl:grid-cols-1">
            <div className="rounded-[24px] border border-blue-200 bg-[linear-gradient(135deg,#EFF6FF_0%,#DBEAFE_100%)] p-5 shadow-[0_16px_34px_rgba(59,130,246,0.14)]">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">
                Applications received
              </div>
              <div className="mt-3 text-5xl font-semibold tracking-[-0.05em] text-slate-900">
                {formatCount(jobDetails.receivedApplications)}
              </div>
              <div className="mt-2 text-sm text-blue-800/80">
                Current sales snapshot
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white/90 p-5 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {timestampLabel}
              </div>
              <div className="mt-2 text-lg font-semibold text-slate-900">
                {formatMaybeDate(Date.now())}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Refreshed when this view was opened
              </div>
            </div>

            {jobDetails.jobLink ? (
              <a
                href={jobDetails.jobLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-[18px] bg-slate-900 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Open public job page
              </a>
            ) : (
              <div className="rounded-[18px] border border-dashed border-slate-300 bg-white/75 px-5 py-4 text-sm text-slate-500">
                No public job link available.
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.55fr),minmax(320px,0.95fr)]">
        <div className="space-y-5">
          <Card className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Job profile
                </div>
                <h3 className="mt-2 text-[22px] font-semibold tracking-[-0.03em] text-slate-900">
                  Hiring requirements
                </h3>
              </div>
              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                Sales view
              </span>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Department
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.department || 'Not provided'}
                </div>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Education
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.education || 'Not provided'}
                </div>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Experience
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.yearsExp || 'Not provided'}
                </div>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Travel required
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.travelRequired || 'Not provided'}
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-[22px] font-semibold tracking-[-0.03em] text-slate-900">
                Role description
              </h3>
              <span className="text-sm text-slate-500">
                Public-facing content
              </span>
            </div>

            <div className="mt-5">
              {hasJobDescription ? (
                <RichTextRenderer
                  value={jobDescription}
                  className="prose prose-slate max-w-none text-[15px] leading-7 prose-headings:tracking-[-0.02em] prose-p:text-slate-700 prose-li:text-slate-700"
                />
              ) : (
                <div className="rounded-[20px] border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No public role description is available for this job.
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Employer context
              </div>
              <h3 className="mt-2 text-[22px] font-semibold tracking-[-0.03em] text-slate-900">
                Workspace and routing details
              </h3>
            </div>

            <div className="mt-5 grid gap-3">
              <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Workspace
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.workspaceName || 'Not provided'}
                </div>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Org ID
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {jobDetails.orgId || 'Unavailable'}
                </div>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Workspace description
              </div>
              <div className="mt-4">
                {hasWorkspaceDescription ? (
                  <RichTextRenderer
                    value={workspaceDescription}
                    className="prose prose-slate max-w-none text-[15px] leading-7 prose-headings:tracking-[-0.02em] prose-p:text-slate-700 prose-li:text-slate-700"
                  />
                ) : (
                  <div className="rounded-[20px] border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                    No workspace description is available for this organization.
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

SalesJobDetailsPanel.defaultProps = {
  headingLabel: DEFAULT_HEADING_LABEL,
  timestampLabel: DEFAULT_TIMESTAMP_LABEL,
};

export default SalesJobDetailsPanel;
